import React, { useMemo, useCallback, useRef } from 'react';
import { observer } from 'mobx-react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Button, Icon } from '@ann/web-components';
import {
  PageContainer,
  ContentContainer,
  ActivityHeaderRow,
  TabSwitcherContainer,
  SearchArea,
  SearchInputWrap,
  SearchInputWrapper,
  SearchIconLeft,
  SearchInputWithClearWrap,
  SearchInputField,
  SearchClearButton,
} from './Activity.styled';
import { ActivitySearchProvider, useActivitySearch } from './context/ActivitySearchContext';
import CommunicationsTab from './pages/CommunicationsTab';
import NotesTab from './pages/NotesTab';

const TABS = {
  NOTES: 'notes',
  COMMUNICATIONS: 'communications',
};

const ActivityHeader = observer(() => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchInputRef = useRef(null);
  const {
    searchValue,
    setSearchValue,
    isExpanded,
    setIsExpanded,
    requestCreateNote,
  } = useActivitySearch();

  const activeTab = useMemo(() => {
    if (location.pathname.includes('/notes')) return TABS.NOTES;
    return TABS.COMMUNICATIONS;
  }, [location.pathname]);

  const handleTabChange = useCallback((tabId) => {
    navigate(tabId, { replace: true });
  }, [navigate]);

  const handleSearchToggle = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, [setIsExpanded]);

  const hasSearchValue = searchValue != null && String(searchValue).length > 0;

  const handleSearchCloseClick = useCallback(() => {
    if (hasSearchValue) {
      setSearchValue('');
      searchInputRef.current?.focus();
    } else {
      setIsExpanded(false);
    }
  }, [hasSearchValue, setSearchValue, setIsExpanded]);

  const handleSearchKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') {
        if (hasSearchValue) {
          setSearchValue('');
        } else {
          setIsExpanded(false);
        }
      }
    },
    [hasSearchValue, setSearchValue, setIsExpanded]
  );

  return (
    <ActivityHeaderRow>
      <TabSwitcherContainer>
        <Button
          icon="person"
          label="Notes"
          pill
          size="small"
          iconLeft="person"
          type={activeTab === TABS.NOTES ? 'primary' : 'secondary'}
          onClick={() => handleTabChange(TABS.NOTES)}
        />
        <Button
          icon="chat_bubble"
          label="Communications"
          pill
          size="small"
          iconLeft="chat_bubble"
          type={activeTab === TABS.COMMUNICATIONS ? 'primary' : 'secondary'}
          onClick={() => handleTabChange(TABS.COMMUNICATIONS)}
        />
      </TabSwitcherContainer>
      <SearchArea>
        {isExpanded ? (
          <SearchInputWrap>
            <SearchInputWrapper>
              <SearchIconLeft aria-hidden>
                <Icon name="search" size="custom" width="16px" height="16px" />
              </SearchIconLeft>
              <SearchInputWithClearWrap>
                <SearchInputField
                  ref={searchInputRef}
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder="Search"
                  autoFocus
                  onKeyDown={handleSearchKeyDown}
                  aria-label="Search"
                />
                <SearchClearButton
                  type="button"
                  onClick={handleSearchCloseClick}
                  aria-label={hasSearchValue ? 'Clear search' : 'Close search'}
                  tabIndex={0}
                >
                  <Icon name="close" size="small" />
                </SearchClearButton>
              </SearchInputWithClearWrap>
            </SearchInputWrapper>
          </SearchInputWrap>
        ) : (
          <Button
            icon="search"
            iconLeft=""
            label=""
            pill
            size="small"
            type="secondary"
            onClick={handleSearchToggle}
          />
        )}
        <Button
          icon="filter_list"
          label="Filters"
          pill
          size="small"
          type="secondary"
          iconRight="tune"
          onClick={() => {}}
        />
        {activeTab === TABS.NOTES && (
          <Button
            icon="add"
            label="Create note"
            iconLeft="plus"
            size="small"
            type="primary"
            onClick={requestCreateNote}
          />
        )}
      </SearchArea>
    </ActivityHeaderRow>
  );
});

const Activity = observer(() => {
  return (
    <PageContainer>
      <ContentContainer>
        <ActivitySearchProvider>
          <ActivityHeader />
          <Routes>
            <Route path="/" element={<Navigate to="notes" replace />} />
            <Route path="notes" element={<NotesTab />} />
            <Route path="communications" element={<CommunicationsTab />} />
          </Routes>
        </ActivitySearchProvider>
      </ContentContainer>
    </PageContainer>
  );
});

export default Activity;
